import {
  isAuthError,
  isForbiddenError,
  isSuccess,
} from '@/context/services/type';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetAiAgentSettingQueryKey,
  UpdateAiAgentServiceSettingInput,
  useGetAiAgentSettingSuspense,
  useUpdateAiAgentServiceSettings,
  useUpdateAiAgentSetting,
} from 'ap-openapi';
import { useCallback, useMemo } from 'react';
import {
  UpdateAiAgentMainSettingResult,
  UpdateAiAgentServicesSettingResult,
} from './type';

export const useGetAiAgentsSettingService = () => {
  const { data, error, isLoading, refetch } = useGetAiAgentSettingSuspense();

  const result = useMemo(
    () => ({
      data: data?.data,
      status: data?.status,
      isLoading,
      refetch,
    }),
    [data, isLoading, refetch]
  );

  // Handle permission errors (401/403) - reload for view
  if (isAuthError(result.status) || isForbiddenError(result.status)) {
    location.reload();
  }

  if (error) throw error;
  return result;
};

export const useUpdateAiAgentMainSetting = () => {
  const { mutateAsync: _mutateAsync, isPending } = useUpdateAiAgentSetting();
  const queryClient = useQueryClient();

  const mutateAsync = useCallback(
    async (isEnabled: boolean): Promise<UpdateAiAgentMainSettingResult> => {
      const resp = await _mutateAsync({
        data: { is_ai_agent_enabled: isEnabled },
      });

      // Always refetch to get current state
      await queryClient.invalidateQueries({
        queryKey: getGetAiAgentSettingQueryKey(),
      });

      return { status: isSuccess(resp.status) ? 'success' : 'error' };
    },
    [_mutateAsync, queryClient]
  );

  return { mutateAsync, isPending };
};

export const useUpdateAiAgentServicesSetting = () => {
  const { mutateAsync: _mutateAsync, isPending } =
    useUpdateAiAgentServiceSettings();
  const queryClient = useQueryClient();

  const mutateAsync = useCallback(
    async (
      data: UpdateAiAgentServiceSettingInput[]
    ): Promise<UpdateAiAgentServicesSettingResult> => {
      const resp = await _mutateAsync({
        data: { ai_agent_service_settings: data },
      });

      // Always refetch to get current state
      await queryClient.invalidateQueries({
        queryKey: getGetAiAgentSettingQueryKey(),
      });

      if (isSuccess(resp.status)) {
        return resp.data ?? { results: [] };
      }
      return { results: [] };
    },
    [_mutateAsync, queryClient]
  );

  return { mutateAsync, isPending };
};
